import type { JSX } from 'react'
import { Link } from '@tanstack/react-router'
import { BarChart3, Camera, Home, PanelLeftClose, PanelLeftOpen, Video, type LucideIcon } from 'lucide-react'
import { useConversationStore, type Conversation } from '@/stores/conversationStore'
import { TeamSwitcher } from './TeamSwitcher'
import Logo from '@/components/ui/logo'
import { CreditsMeter } from '@/components/layout/CreditsMeter'
import { AvatarMenu } from '@/components/layout/AvatarMenu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { studioBOn } from '@/lib/studioB/flag'
import { IconTipB } from '@/components/studio/IconTipB'

interface SidebarProps {
  onChatSelect?: () => void
  showConversations?: boolean
  /** The icon strip: icons only, each with a tooltip. */
  collapsed?: boolean
  /** Collapses or expands the rail; the drawer has none. */
  onToggle?: () => void
  showLogo?: boolean
}

const activeProps = { className: 'bg-primary/10 text-primary' }
const inactiveProps = { className: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground' }

function Tip({ label, show, children }: { label: string; show: boolean; children: JSX.Element }) {
  if (studioBOn()) return <IconTipB label={label} side="right" off={!show}>{children}</IconTipB>
  if (!show) return children
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  )
}

type NavItemProps = { to: '/' | '/videos' | '/captures' | '/analytics'; icon: LucideIcon; label: string; exact?: boolean; count?: number; collapsed: boolean; onClick?: () => void }

/** Studio B's rail: the row is the rail's width, so its pill wipes with the edge; the icon's centre is 32 px in (8 px of rail,
 *  16 of row, half the icon), the centre of a collapsed rail; the words keep their open width and fade. */
function WipeNavItem({ to, icon: Icon, label, exact, count, collapsed, onClick }: NavItemProps) {
  return (
    <Tip label={label} show={collapsed}>
      <Link
        to={to}
        search={to === '/' ? { slide: undefined } : undefined}
        activeOptions={{ exact, includeSearch: false }}
        activeProps={activeProps}
        inactiveProps={inactiveProps}
        aria-label={label}
        className="flex h-9 w-full items-center overflow-hidden rounded-lg pl-4 text-sm font-medium transition-colors"
        onClick={onClick}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span data-rail-label className="ml-2.5 flex w-[164px] shrink-0 items-center gap-2 whitespace-nowrap">
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {!!count && <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{count}</span>}
        </span>
      </Link>
    </Tip>
  )
}

function NavItem({ to, icon: Icon, label, exact, count, collapsed, onClick }: NavItemProps) {
  return (
    <Tip label={label} show={collapsed}>
      <Link
        to={to}
        search={to === '/' ? { slide: undefined } : undefined}
        activeOptions={{ exact, includeSearch: false }}
        activeProps={activeProps}
        inactiveProps={inactiveProps}
        aria-label={label}
        className={cn('flex h-9 min-w-0 items-center rounded-lg text-sm font-medium transition-colors', collapsed ? 'justify-center' : 'gap-2.5 px-2.5')}
        onClick={onClick}
      >
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
        {!collapsed && !!count && <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{count}</span>}
      </Link>
    </Tip>
  )
}

function Recent({ videos, onChatSelect }: { videos: Conversation[]; onChatSelect?: () => void }) {
  const setActiveConversation = useConversationStore((s) => s.setActiveConversation)
  return (
    <>
      <h3 className="mb-1 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Recent</h3>
      <nav className="grid min-w-0 gap-0.5">
        {videos.slice(0, 5).map((conversation) => (
          <Link
            key={conversation.id}
            to="/chat/$conversationId/video-workspace"
            params={{ conversationId: conversation.id }}
            activeProps={activeProps}
            inactiveProps={inactiveProps}
            title={conversation.title}
            className="flex h-8 min-w-0 items-center rounded-lg px-2.5 text-sm transition-colors"
            onClick={() => {
              setActiveConversation(conversation.id)
              onChatSelect?.()
            }}
          >
            <span className="min-w-0 truncate">{conversation.title}</span>
          </Link>
        ))}
      </nav>
    </>
  )
}

const TOGGLE = 'grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground'

/**
 * Studio B's rail collapses as a wipe: it lays out the same open or collapsed, so nothing re-centres or reflows, and the
 * rail clips it. Words fade ([data-rail-label]); what only a collapsed rail shows waits in its place ([data-rail-compact]).
 * Open-width pieces are 216 px, the open rail less its 8 px sides.
 */
function RailB({ collapsed, onToggle, onChatSelect, videos }: { collapsed: boolean; onToggle: () => void; onChatSelect?: () => void; videos: Conversation[] }) {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-full min-w-0 flex-col overflow-y-auto overflow-x-hidden px-2 py-3.5">
        <div className="relative mb-2 flex h-9 w-[216px] items-center">
          <Link to="/" search={{ slide: undefined }} onClick={onChatSelect} aria-label="Home" className="flex h-9 items-center pl-1">
            <Logo variant="modern" className="h-6" />
          </Link>
          <span data-rail-label className="ml-auto">
            <Tip label="Collapse sidebar" show={false}>
              <button type="button" onClick={onToggle} aria-label="Collapse sidebar" className={TOGGLE}>
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </Tip>
          </span>
          <span data-rail-compact className="absolute left-0 top-0">
            <Tip label="Expand sidebar" show={collapsed}>
              <button
                type="button"
                onClick={onToggle}
                aria-label="Expand sidebar"
                className="group grid h-9 w-12 place-items-center rounded-lg text-b-t2 transition-colors hover:bg-b-raised hover:text-b-t1 focus-visible:bg-b-raised"
              >
                <PanelLeftOpen className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
              </button>
            </Tip>
          </span>
        </div>
        <nav className="grid min-w-0 gap-0.5">
          <WipeNavItem to="/" icon={Home} label="Home" exact collapsed={collapsed} onClick={onChatSelect} />
          <WipeNavItem to="/videos" icon={Video} label="Videos" count={videos.length} collapsed={collapsed} onClick={onChatSelect} />
          <WipeNavItem to="/captures" icon={Camera} label="Captures" collapsed={collapsed} onClick={onChatSelect} />
          <WipeNavItem to="/analytics" icon={BarChart3} label="Analytics" collapsed={collapsed} onClick={onChatSelect} />
        </nav>

        <div data-rail-label className="w-[216px]">
          <TeamSwitcher className="px-1 pt-4" />
        </div>

        {videos.length > 0 && (
          <div data-rail-label className="mt-5 w-[216px]">
            <Recent videos={videos} onChatSelect={onChatSelect} />
          </div>
        )}

        <div className="mt-auto grid min-w-0 gap-2 pt-4">
          <div className="grid items-end">
            <div data-rail-label className="col-start-1 row-start-1 w-[216px]"><CreditsMeter variant="rail" /></div>
            <div data-rail-compact className="col-start-1 row-start-1 w-12"><CreditsMeter variant="icon" /></div>
          </div>
          <AvatarMenu wipe side="right" beside={20} theme />
        </div>
      </div>
    </TooltipProvider>
  )
}

export function Sidebar({ onChatSelect, showConversations = true, collapsed = false, onToggle, showLogo = true }: SidebarProps) {
  const { conversations } = useConversationStore()
  const videoConversations = conversations.filter(c => c.collectionType === 'VIDEO')
  if (studioBOn() && onToggle) {
    return <RailB collapsed={collapsed} onToggle={onToggle} onChatSelect={onChatSelect} videos={showConversations ? videoConversations : []} />
  }
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose
  const toggleLabel = collapsed ? 'Expand sidebar' : 'Collapse sidebar'

  return (
    <TooltipProvider delayDuration={200}>
      <div className={cn('flex h-full min-w-0 flex-col overflow-y-auto overflow-x-hidden py-3.5', collapsed ? 'items-stretch px-2' : 'px-2.5')}>
        <div className={cn('mb-2 flex min-h-9 min-w-0 items-center', collapsed ? 'justify-center' : 'gap-1')}>
          {!collapsed && showLogo && (
            <Link to="/" search={{ slide: undefined }} onClick={onChatSelect} className="flex min-w-0 flex-1 items-center px-2 py-1" title="Home">
              <Logo variant="modern" className="h-7" />
            </Link>
          )}
          {onToggle && (
            <Tip label={toggleLabel} show={collapsed}>
              <button
                type="button"
                onClick={onToggle}
                aria-label={toggleLabel}
                className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground', !collapsed && 'ml-auto')}
              >
                <ToggleIcon className="h-4 w-4" />
              </button>
            </Tip>
          )}
        </div>
        {!collapsed && <TeamSwitcher className="px-1 pb-3" />}

        <nav className="grid min-w-0 gap-0.5">
          <NavItem to="/" icon={Home} label="Home" exact collapsed={collapsed} onClick={onChatSelect} />
          <NavItem to="/videos" icon={Video} label="Videos" count={videoConversations.length} collapsed={collapsed} onClick={onChatSelect} />
          <NavItem to="/captures" icon={Camera} label="Captures" collapsed={collapsed} onClick={onChatSelect} />
          <NavItem to="/analytics" icon={BarChart3} label="Analytics" collapsed={collapsed} onClick={onChatSelect} />
        </nav>

        {!collapsed && showConversations && videoConversations.length > 0 && (
          <div className="mt-5 min-w-0">
            <Recent videos={videoConversations} onChatSelect={onChatSelect} />
          </div>
        )}

        <div className={cn('mt-auto grid min-w-0 gap-2 pt-4', collapsed && 'justify-items-center')}>
          <CreditsMeter variant={collapsed ? 'icon' : 'rail'} />
          <AvatarMenu compact={collapsed} side={collapsed ? 'right' : 'top'} theme={studioBOn()} />
        </div>
      </div>
    </TooltipProvider>
  )
}
